// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Feature flags for work that is built but not shown yet. Set per browser in
 * `localStorage['rux.flags']` as JSON, e.g. `{"submission":true}`; anything
 * unreadable is the defaults (everything off).
 *
 *  - `submission`: the bygningsaffald.dk flow in Rapporter & indberetning —
 *    the Del 1 / Del 2 checklist, "Indberet til bygningsaffald.dk" with its
 *    notice, and the Materialeflow leaf of the tree.
 *
 * Read once per mount (a changed flag takes a reload). Pure apart from
 * `storedFlags`, which is the only place that touches `localStorage`.
 */

export interface FeatureFlags {
  submission: boolean;
}

export const FLAGS_KEY = 'rux.flags';

export const DEFAULT_FLAGS: Readonly<FeatureFlags> = Object.freeze({ submission: false });

/** The flags a stored value describes; malformed JSON or a non-object is the defaults. */
export function parseFlags(raw: string | null): FeatureFlags {
  if (raw === null) return { ...DEFAULT_FLAGS };
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_FLAGS };
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return { ...DEFAULT_FLAGS };
  const v = value as Record<string, unknown>;
  return { submission: typeof v.submission === 'boolean' ? v.submission : DEFAULT_FLAGS.submission };
}

/** The flags in `storage`; a storage that throws (private mode, blocked) is the defaults. */
export function readFlags(storage: Pick<Storage, 'getItem'> | null): FeatureFlags {
  if (!storage) return { ...DEFAULT_FLAGS };
  try {
    return parseFlags(storage.getItem(FLAGS_KEY));
  } catch {
    return { ...DEFAULT_FLAGS };
  }
}

/** This browser's flags (`useState(storedFlags)`). Touching `localStorage` itself may throw too. */
export function storedFlags(): FeatureFlags {
  try {
    return readFlags(typeof localStorage === 'undefined' ? null : localStorage);
  } catch {
    return { ...DEFAULT_FLAGS };
  }
}
